/**
 * IDF (Input Data File) Parser Types
 *
 * These types represent the structured data model for EnergyPlus IDF files.
 * The parser converts raw IDF text into this model, and the serializer
 * converts it back to valid IDF text.
 */

/**
 * Represents a single field within an IDF object.
 * Fields are positional (order matters) and may have inline comments.
 */
export interface IDFField {
  /** The raw string value of the field */
  value: string;
  /** Inline comment after the field (e.g., "!- Name") */
  inlineComment?: string;
  /** Units parsed from the comment (e.g., "{deg}", "{W}") */
  units?: string;
}

/**
 * Source location information for error reporting and editor integration.
 */
export interface SourceLocation {
  /** Starting line number (1-indexed) */
  startLine: number;
  /** Ending line number (1-indexed) */
  endLine: number;
  /** Starting column (0-indexed) */
  startColumn?: number;
  /** Ending column (0-indexed) */
  endColumn?: number;
}

/**
 * Represents a single IDF object (e.g., Building, Zone, Material).
 * Each object has a class name and a list of positional fields.
 */
export interface IDFObject {
  /** Unique identifier for editing and tracking */
  id: string;
  /** Object class name (e.g., "Building", "Zone", "Schedule:Compact") */
  className: string;
  /** Ordered list of field values */
  fields: IDFField[];
  /** Standalone comments appearing before this object */
  leadingComments: string[];
  /** Source location for error reporting */
  sourceLocation?: SourceLocation;
}

/**
 * Represents an entire IDF document.
 */
export interface IDFDocument {
  /** EnergyPlus version (parsed from Version object) */
  version: string;
  /** All objects in the document, in order */
  objects: IDFObject[];
  /** Comments at the start of the file before any objects */
  headerComments: string[];
  /** Trailing comments at the end of the file */
  trailingComments: string[];
}

/**
 * Parser error with location information.
 */
export interface ParseError {
  /** Error message */
  message: string;
  /** Line number where error occurred (1-indexed) */
  line: number;
  /** Column where error occurred (0-indexed) */
  column?: number;
  /** Severity level */
  severity: 'error' | 'warning';
}

/**
 * Result of parsing an IDF file.
 */
export interface ParseResult {
  /** The parsed document (may be partial if errors occurred) */
  document: IDFDocument;
  /** Any errors encountered during parsing */
  errors: ParseError[];
  /** Whether parsing completed successfully */
  success: boolean;
}

/**
 * Token types for the lexer.
 */
export enum TokenType {
  /** Object class name (e.g., "Building") */
  ClassName = 'ClassName',
  /** Field value */
  FieldValue = 'FieldValue',
  /** Comma separator between fields */
  Comma = 'Comma',
  /** Semicolon terminating an object */
  Semicolon = 'Semicolon',
  /** Comment (standalone or inline) */
  Comment = 'Comment',
  /** End of file */
  EOF = 'EOF',
  /** Newline character(s) */
  Newline = 'Newline',
  /** Whitespace (spaces, tabs) */
  Whitespace = 'Whitespace',
}

/**
 * A single token produced by the lexer.
 */
export interface Token {
  /** Type of the token */
  type: TokenType;
  /** Raw text value of the token */
  value: string;
  /** Line number (1-indexed) */
  line: number;
  /** Column number (0-indexed) */
  column: number;
}

/**
 * Options for the serializer.
 */
export interface SerializerOptions {
  /** Whether to produce minified output */
  minified?: boolean;
  /** Indentation string (default: "  ") */
  indent?: string;
  /** Whether to align field comments */
  alignComments?: boolean;
  /** Target column for comment alignment (default: 25) */
  commentColumn?: number;
  /** Whether to preserve original formatting where possible */
  preserveFormatting?: boolean;
}
